import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, UserCircle2 } from "lucide-react";
import { setAuthToken } from "@/lib/api";

type LoginResponse = {
  token: string;
  user: string;
  displayName?: string;
  roles: string[];
  expiresInSeconds: number;
};

type LoginError = { error?: string };

/**
 * Standalone login. Posts username + password to /api/v1/auth/login, which
 * verifies against the seeded user table (bcrypt) and returns an HS256 JWT.
 * External-IdP profiles bypass this page entirely.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const resp = await fetch("/api/v1/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (resp.status === 401) {
        const body = (await resp.json().catch(() => ({}))) as LoginError;
        throw new Error(body.error ?? "Invalid credentials");
      }
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as LoginError;
        throw new Error(body.error ?? `Login failed (HTTP ${resp.status})`);
      }
      const data = (await resp.json()) as LoginResponse;
      setAuthToken(data.token);
      navigate("/chat", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-[calc(100vh-57px)] max-w-md items-center justify-center px-6">
      <form
        onSubmit={onSubmit}
        className="w-full space-y-5 rounded-xl border border-datumcraft-slate/25 bg-white p-7 shadow-lg"
      >
        <div>
          <h1 className="font-display text-xl font-semibold text-datumcraft-navy">Sign in</h1>
          <p className="mt-1 text-xs text-datumcraft-slate">
            DatumCraft Operate — enter your administrator credentials.
          </p>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-datumcraft-ink">Username</span>
          <div className="flex items-center gap-2 rounded-md border border-datumcraft-slate/40 px-3 py-2 focus-within:border-datumcraft-navy focus-within:ring-1 focus-within:ring-datumcraft-navy/30">
            <UserCircle2 size={14} className="text-datumcraft-slate" />
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-transparent text-sm outline-none"
              autoFocus
              autoComplete="username"
            />
          </div>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-datumcraft-ink">Password</span>
          <div className="flex items-center gap-2 rounded-md border border-datumcraft-slate/40 px-3 py-2 focus-within:border-datumcraft-navy focus-within:ring-1 focus-within:ring-datumcraft-navy/30">
            <KeyRound size={14} className="text-datumcraft-slate" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent text-sm outline-none"
              autoComplete="current-password"
            />
          </div>
        </label>

        {error && (
          <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !username.trim() || !password}
          className="w-full rounded-md bg-datumcraft-navy px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-datumcraft-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>

        <p className="text-center text-2xs text-datumcraft-slate">
          First-time login? The default admin is <code className="font-mono">admin</code> /{" "}
          <code className="font-mono">admin</code>. Change it in <code>conf/.env</code>.
        </p>
      </form>
    </div>
  );
}
